// Lead times - SINGLE source of truth for how long each product takes to reach
// the customer. Import these anywhere a lead time is stated (product cards,
// product pages, the awnings hero eyebrow, the "How It's Made" note, the FAQ,
// and the quote forms) so the numbers can never drift out of sync again.
//
// Policy (owner, 2026-09-27):
//   - Anything held in stock ships the next business day.
//   - Made to order, 3 m or less (width and projection): 2 to 3 weeks.
//   - Made to order, above 3 m in width or projection: 6 to 8 weeks.
//     Size is the only thing that moves an awning between the two bands - a
//     custom size of 3 m or less is 2 to 3 weeks like any other.
//   - Pergolas are fabricated to order: 6 to 8 weeks.
//
// "In stock" is NEVER assumed from a colour name - it is driven by the real
// per-variant inventory (see utils/stock.js). A variant only shows the
// next-business-day message when its (width x colour) inventory quantity is
// greater than zero; otherwise it falls back to the made-to-order lead time.
//
// Australian English throughout: hyphens (never em dashes), and the "1 to 3
// weeks" form (never "1-3 weeks" or en dashes).

// --- Raw lead-time phrases ------------------------------------------------
export const LEAD_STOCK = 'next business day';   // held in stock (qty > 0)
export const LEAD_STANDARD = '2 to 3 weeks';     // made to order, 3 m or less
export const LEAD_LARGE = '6 to 8 weeks';        // made to order, above 3 m
export const LEAD_PERGOLA = '6 to 8 weeks';      // pergolas, fabricated to order

// The colour ASG holds as ready stock. Used ONLY for customer-facing copy;
// the actual in-stock decision is always driven by real inventory, not this.
export const STOCK_COLOUR_NAME = 'Charcoal grey';

// Awnings wider or deeper than this (metres) take LEAD_LARGE when made to
// order rather than LEAD_STANDARD.
export const STANDARD_SIZE_MAX_M = 3;

// --- Composed customer-facing phrases -------------------------------------
// Built from the values above so wording stays consistent everywhere.
export const STOCK_BANNER_TITLE = `${STOCK_COLOUR_NAME} held in stock - often ships ${LEAD_STOCK}`;
export const STOCK_SHORT = `${STOCK_COLOUR_NAME} often ships ${LEAD_STOCK}`;
export const IN_STOCK_BADGE = `In Stock - Ships ${LEAD_STOCK}`;
export const MADE_TO_ORDER_SHORT = `Made to order - ${LEAD_STANDARD}`;
// Both made-to-order bands in one phrase, for places that talk about the
// range rather than one configured size.
export const MADE_TO_ORDER_BANDS = `${LEAD_STANDARD} up to ${STANDARD_SIZE_MAX_M} m, ${LEAD_LARGE} above that`;

// Shown on the colours page and on every awning product page, so the stock
// position is stated wherever a colour is being chosen.
export const COLOUR_LEAD_TIME_NOTE = `${STOCK_COLOUR_NAME} is held in stock and can often ship ${LEAD_STOCK}. Everything else is made to order: ${MADE_TO_ORDER_BANDS}.`;

// Resolve the lead-time phrase for a specific configured awning variant.
//   width, projection - selected size in metres (number or numeric string)
//   inStock           - this exact (width x colour) has inventory qty > 0
//   custom            - the size was typed in rather than picked; it no longer
//                       changes the answer (size alone does), but is kept so
//                       callers need not change
// In-stock wins: the unit is physically in the warehouse, so it ships next
// business day regardless of size. Otherwise it is made to order, and the
// size decides the band. A size that cannot be read gets the longer band, so
// the site never promises sooner than it can deliver.
export function awningLeadTime({ width, projection, inStock = false } = {}) {
  if (inStock) return LEAD_STOCK;
  const w = Number(width);
  const p = Number(projection);
  if (!Number.isFinite(w) || !Number.isFinite(p)) return LEAD_LARGE;
  return w > STANDARD_SIZE_MAX_M || p > STANDARD_SIZE_MAX_M ? LEAD_LARGE : LEAD_STANDARD;
}
